import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import Swal from "sweetalert2";

const GET_SETTINGS = gql`
  query {
    tenantSignupAlertSettings {
      destinationEmail
      destinationPhone
      emailEnabled
      smsEnabled
      updatedAt
    }
  }
`;

const UPDATE_SETTINGS = gql`
  mutation UpdateTenantSignupAlertSettings(
    $destinationEmail: String
    $destinationPhone: String
    $emailEnabled: Boolean!
    $smsEnabled: Boolean!
  ) {
    updateTenantSignupAlertSettingsMutation(
      destinationEmail: $destinationEmail
      destinationPhone: $destinationPhone
      emailEnabled: $emailEnabled
      smsEnabled: $smsEnabled
    ) {
      destinationEmail
      destinationPhone
      emailEnabled
      smsEnabled
      updatedAt
    }
  }
`;

export default function NotificationAlertSettings() {
  const [destinationEmail, setDestinationEmail] = useState("");
  const [emailEnabled, setEmailEnabled] = useState(false);

  const [fetchSettings, { data, error, loading }] = useLazyQuery(GET_SETTINGS, {
    fetchPolicy: "network-only",
  });

  const [updateSettings, { loading: saving }] = useMutation(UPDATE_SETTINGS, {
    onCompleted: () => {
      Swal.fire({
        title: "Saved!",
        text: "Notification settings updated successfully.",
        confirmButtonColor: " #A1368B",
        icon: "success",
        confirmButtonText: "OK",
      });
    },
    onError: (err) => {
      Swal.fire({
        title: "Error!",
        text: `Save failed: ${err.message}`,
        icon: "error",
        confirmButtonText: "OK",
      });
    },
    refetchQueries: [{ query: GET_SETTINGS }],
  });

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (data?.tenantSignupAlertSettings) {
      setDestinationEmail(data.tenantSignupAlertSettings.destinationEmail || "");
      setEmailEnabled(!!data.tenantSignupAlertSettings.emailEnabled);
    }
  }, [data]);

  const handleSave = () => {
    updateSettings({
      variables: {
        destinationEmail,
        destinationPhone: "",
        emailEnabled,
        // SMS has no platform sender configured yet - always saved as off.
        smsEnabled: false,
      },
    });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 2 }} fontWeight={700}>
        New tenant signup alerts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Notify the Acolide team whenever a new tenant business signs up.
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      ) : null}
      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <CardContent>
          {loading ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={32} />
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={emailEnabled}
                    onChange={(e) => setEmailEnabled(e.target.checked)}
                  />
                }
                label="Notify by email"
              />
              <TextField
                label="Destination email"
                value={destinationEmail}
                onChange={(e) => setDestinationEmail(e.target.value)}
                fullWidth
                placeholder="team@acolide.com"
              />

              <FormControlLabel
                control={<Switch checked={false} disabled />}
                label="Notify by SMS"
              />
              <TextField
                label="Destination phone"
                value=""
                fullWidth
                disabled
                placeholder="Coming soon"
                helperText="SMS notifications are coming soon - a platform SMS sender isn't configured yet."
              />

              <Box>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
